import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  UploadIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XIcon,
} from "@heroicons/react/outline";
import { HollowDotsSpinner } from "react-epic-spinners";
import fire from "../../utils/Toastr";
import {
  buildBulkPayload,
  downloadBulkTemplate,
  parseBulkFile,
  parseRowsForColumns,
  validateBulkRows,
} from "../../utils/referralBulkUpload";

const ACCEPTED_EXTENSIONS = ["csv", "xlsx", "xls"];

const StatCard = ({ label, value, tone }) => {
  const tones = {
    green: "bg-green-50 text-green-700 border-green-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[tone] || tones.blue}`}>
      <p className="text-xs font-medium uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value ?? 0}</p>
    </div>
  );
};

export default function BulkUploadModal({
  isOpen,
  onClose,
  title,
  config,
  onSubmit,
  onSuccess,
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [missingColumns, setMissingColumns] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const resetState = useCallback(() => {
    setFileName("");
    setRows([]);
    setMissingColumns([]);
    setParsing(false);
    setSubmitting(false);
    setDragActive(false);
    setResult(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const invalidRows = rows.filter((row) => row.__errors?.length > 0);
  const canSubmit =
    !parsing &&
    !submitting &&
    missingColumns.length === 0 &&
    rows.length > 0 &&
    invalidRows.length === 0;

  const handleFile = async (file) => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      fire("error", "Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    setParsing(true);
    setResult(null);
    setFileName(file.name);

    try {
      const sheetRows = await parseBulkFile(file);
      const { rows: parsedRows, missingColumns: missing } =
        parseRowsForColumns(sheetRows, config.columns);

      if (missing.length > 0) {
        setRows([]);
        setMissingColumns(missing);
        fire(
          "error",
          `Missing required column(s): ${missing
            .map((column) => column.label)
            .join(", ")}`
        );
        return;
      }

      const validatedRows = validateBulkRows(parsedRows, config.columns);
      setMissingColumns([]);
      setRows(validatedRows);

      if (validatedRows.length === 0) {
        fire("warning", "No data rows found in the selected file");
      }
    } catch (error) {
      console.error("Error parsing bulk upload file:", error);
      setRows([]);
      fire("error", "Unable to read the file. Please use the provided template.");
    } finally {
      setParsing(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleClose = () => {
    const queued = Number(result?.totalQueued) || 0;
    resetState();
    onClose();
    if (queued > 0) {
      onSuccess?.();
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const payload = buildBulkPayload(rows, config.columns);
      const response = await onSubmit(payload);

      if (response && response.status !== false && response.data) {
        setResult(response.data);
        fire("success", response.message || "Bulk upload completed");
      } else {
        fire("error", response?.message || "Bulk upload failed");
      }
    } catch (error) {
      console.error("Error submitting bulk upload:", error);
      fire("error", "Bulk upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = (format) => {
    downloadBulkTemplate({
      baseName: config.templateBaseName,
      columns: config.columns,
      sampleRow: config.sampleRow,
      format,
    });
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (!submitting) handleClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-4xl transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      {title}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      Accepted formats: .csv, .xlsx, .xls
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-6 py-5">
                  {result ? (
                    <div>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        <p className="ml-2 text-sm font-medium text-gray-900">
                          Upload processed
                        </p>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <StatCard
                          label="Total received"
                          value={result.totalReceived}
                          tone="blue"
                        />
                        <StatCard
                          label="Total queued"
                          value={result.totalQueued}
                          tone="green"
                        />
                        <StatCard
                          label="Total rejected"
                          value={result.totalRejected}
                          tone="red"
                        />
                      </div>

                      {Array.isArray(result.rejectedRows) &&
                        result.rejectedRows.length > 0 && (
                          <div className="mt-5">
                            <div className="flex items-center">
                              <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                              <p className="ml-2 text-sm font-medium text-gray-900">
                                Rejected rows
                              </p>
                            </div>
                            <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    {config.rejectedColumns.map((column) => (
                                      <th
                                        key={column.key}
                                        className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                      >
                                        {column.label}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                  {result.rejectedRows.map((rejected, index) => (
                                    <tr key={`${rejected.rowNumber}-${index}`}>
                                      {config.rejectedColumns.map((column) => (
                                        <td
                                          key={column.key}
                                          className="whitespace-nowrap px-3 py-2 text-sm text-gray-700"
                                        >
                                          {rejected[column.key] ?? "N/A"}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                    </div>
                  ) : (
                    <div>
                      <div className="rounded-md border border-blue-100 bg-blue-50 px-4 py-3">
                        <p className="text-sm text-blue-800">
                          Download a template below, fill in your{" "}
                          {config.resourceLabel}, then upload the completed
                          file. The first sheet and header row are used.
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => downloadTemplate("csv")}
                            className="inline-flex items-center rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                          >
                            Download CSV template
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadTemplate("xlsx")}
                            className="inline-flex items-center rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
                          >
                            Download Excel template
                          </button>
                        </div>
                      </div>

                      <label
                        className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 text-center ${
                          dragActive
                            ? "border-red-400 bg-red-50"
                            : "border-gray-300 bg-white hover:border-gray-400"
                        }`}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragActive(true);
                        }}
                        onDragLeave={() => setDragActive(false)}
                        onDrop={handleDrop}
                      >
                        <UploadIcon className="h-8 w-8 text-gray-400" />
                        <span className="mt-2 text-sm text-gray-600">
                          {fileName || "Click to select or drag and drop a file"}
                        </span>
                        <span className="mt-3 inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                          Choose file
                        </span>
                        <input
                          ref={inputRef}
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          className="hidden"
                          onChange={(event) =>
                            handleFile(event.target.files?.[0])
                          }
                        />
                      </label>

                      {parsing && (
                        <div className="mt-4 flex items-center justify-center py-6">
                          <HollowDotsSpinner size={30} color="#4D627E" />
                        </div>
                      )}

                      {!parsing && rows.length > 0 && (
                        <div className="mt-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm text-gray-700">
                              {rows.length} row{rows.length === 1 ? "" : "s"} found
                            </p>
                            {invalidRows.length > 0 ? (
                              <p className="text-sm font-medium text-red-600">
                                {invalidRows.length} row
                                {invalidRows.length === 1 ? "" : "s"} need attention
                              </p>
                            ) : (
                              <p className="text-sm font-medium text-green-600">
                                All rows are valid
                              </p>
                            )}
                          </div>

                          <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="sticky top-0 bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                                    Row
                                  </th>
                                  {config.columns.map((column) => (
                                    <th
                                      key={column.key}
                                      className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
                                    >
                                      {column.label}
                                    </th>
                                  ))}
                                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {rows.map((row) => (
                                  <tr
                                    key={row.__rowNumber}
                                    className={
                                      row.__errors?.length > 0 ? "bg-red-50" : ""
                                    }
                                  >
                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
                                      {row.__rowNumber}
                                    </td>
                                    {config.columns.map((column) => (
                                      <td
                                        key={column.key}
                                        className="whitespace-nowrap px-3 py-2 text-sm text-gray-700"
                                      >
                                        {row[column.key] || "—"}
                                      </td>
                                    ))}
                                    <td className="px-3 py-2 text-sm">
                                      {row.__errors?.length > 0 ? (
                                        <span className="text-red-600">
                                          {row.__errors.join("; ")}
                                        </span>
                                      ) : (
                                        <span className="text-green-600">Valid</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                  {result ? (
                    <button
                      type="button"
                      onClick={handleClose}
                      className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
                    >
                      Done
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={submitting}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {submitting && (
                          <HollowDotsSpinner
                            size={12}
                            color="#FFFFFF"
                            className="mr-2"
                          />
                        )}
                        {submitting ? "Submitting..." : "Submit Upload"}
                      </button>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
